import React from 'react'
import { useIntl } from 'react-intl'
import TimeAgo, { type Formatter } from 'react-timeago'

import { CancelledStatus } from '@/components/atoms/CancelledStatus/CancelledStatus.tsx'
import { Car, type CarProps } from '@/components/atoms/Car/Car.tsx'
import { Donation, type DonationProps } from '@/components/atoms/Donation/Donation.tsx'
import { Icons } from '@/components/atoms/Icons/Icons.tsx'
import { Pin, type PinProps } from '@/components/atoms/Pin/Pin.tsx'
import { cn } from '@/lib/utils.ts'

export type TeamCarData = Pick<CarProps, 'carNumber' | 'carType' | 'vip'>

export interface StatusState {
  selectedTeam?: TeamCarData
}

export type CompletedTransportRequestStatus = 'unfindabled' | 'cancelled' | 'completed'

export type TransportRequestStatus =
  'pending' | 'assigned' | 'allocated' | 'available' | 'onboarded' | CompletedTransportRequestStatus

export interface StatusData {
  status: TransportRequestStatus
  description?: string
  since?: Date
  team?: TeamCarData
  pin?: Pick<PinProps, 'letter'>
  donation?: DonationProps
  willExecuteAt?: Date
}

export interface TimeAgoFormat {
  timeAgoFormatter?: Formatter
  willExecuteInTimeAgoFormatter?: Formatter
}

export interface StatusProps extends React.HtmlHTMLAttributes<HTMLDivElement>, StatusState, StatusData, TimeAgoFormat {}

export const Status = React.forwardRef<HTMLDivElement, StatusProps>(
  (
    {
      className,
      status,
      description,
      since,
      team = { carNumber: '' },
      selectedTeam,
      pin,
      donation,
      willExecuteAt,
      timeAgoFormatter,
      willExecuteInTimeAgoFormatter,
      ...props
    },
    ref
  ) => {
    const intl = useIntl()
    const carState =
      status === 'assigned'
        ? 'assigned'
        : status === 'onboarded'
          ? 'onboarded'
          : status === 'pending' && !selectedTeam
            ? 'pending'
            : status === 'pending' && selectedTeam
              ? 'available'
              : status === 'available'
                ? 'available'
                : status === 'allocated'
                  ? 'allocated'
                  : status === 'unfindabled'
                    ? 'unfindabled'
                    : status === 'completed' && team.carNumber
                      ? 'completedSuccess'
                      : status === 'cancelled' && team.carNumber
                        ? 'completedFailure'
                        : ''

    const pinState =
      status === 'assigned'
        ? 'assigned'
        : status === 'onboarded'
          ? 'onboarded'
          : status === 'pending'
            ? 'pending'
            : status === 'available'
              ? 'pending'
              : status === 'allocated'
                ? 'allocated'
                : status === 'unfindabled'
                  ? 'unfindabled'
                  : ''

    let carFrom
    let carTo = team

    if (selectedTeam) {
      if (status !== 'pending') {
        carFrom = team
      }

      carTo = selectedTeam
    }

    return (
      <div className={cn('flex w-fit flex-col items-start gap-1 text-left', className)} ref={ref} {...props}>
        <p className="w-full min-w-[130px] text-sm text-gray-800 select-none">
          {description}
          {since && <TimeAgo formatter={timeAgoFormatter} date={since} title={intl.formatTime(since)} maxPeriod={15} />}
        </p>
        {carState && pinState ? (
          <div className="flex items-center gap-2">
            {carFrom && (
              <>
                <Car state={carState} {...carFrom} />
                <Icons.doubleArrows className="h-2 w-4 fill-black" />
              </>
            )}
            <Car state={carState} {...carTo} />
            <Icons.chevronRight className="h-4 w-3 stroke-gray-400" />
            <Pin state={pinState} {...pin} />
          </div>
        ) : (
          <div className="flex items-center gap-3">
            {carState ? <Car state={carState} {...carTo} /> : <CancelledStatus />}
            {donation && <Donation {...donation} />}
          </div>
        )}
        <p className="-my-1 w-full min-w-[130px] text-sm text-green-500 select-none">
          {willExecuteAt && (
            <TimeAgo
              formatter={willExecuteInTimeAgoFormatter}
              date={willExecuteAt}
              title={intl.formatTime(willExecuteAt)}
              maxPeriod={15}
            />
          )}
        </p>
      </div>
    )
  }
)
